// client/src/lib/routeScroll.ts
/**
 * Where the window scroll goes when the screen changes — ONE implementation for every shell
 * (v2 client, v2 manager, crew, the classic web layouts). App audit 2026-10-01 (TestFlight build 3):
 *
 *  - A new screen must open at the TOP. The v2 shells reset with a single `window.scrollTo(0, 0)`
 *    in a passive effect; iOS ignores scrollTo while a touch-momentum fling is still running, so a
 *    tap on the dock mid-fling could land the next screen at the old offset. The classic web layouts
 *    had no reset at all (managers landed mid-page on every navigation — EM-5).
 *  - A TAB switch that only changes `?tab=` is not a route change (wouter's path has no query), so
 *    the finance hub kept the old offset and opened a short tab below its own content (EM-2).
 *
 * Browser back/forward (popstate) is left to the browser in the classic layouts: restoring the
 * previous offset there is what a person expects from Back.
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "wouter";

/** How recently the document must have scrolled for a momentum fling to be plausibly still running. */
export const FLING_WINDOW_MS = 150;
/** How long after a reset a stale offset handed back by WebKit is still undone (R8-1: seen at 32–76 ms). */
export const ECHO_GUARD_MS = 600;
let lastScrollAt = 0;
/** Armed by a reset: until `echoGuardUntil`, a scroll back to the pre-reset offset is undone. */
let echoGuardUntil = 0;
let echoFrom = 0;

/**
 * Audit R8-1 (iOS app, measured in the simulator with a per-frame scroll timeline): the reset DOES
 * land (scrollY 0 two milliseconds after the navigation), but 30–80 ms later — when the new page's
 * first layer-tree commit reaches WKWebView's UI process — a scroll event hands back the OLD offset
 * (/me: 0 → 5417 → 0 → 1072 = 1946 − 874; /bookings: 0 → 744 = 1618 − 874), i.e. the scroll view's
 * stale position, clamped to the new content size. Nothing in the page asked for it. This says
 * whether a scroll seen right after a reset is that echo: back to (or clamped from) the pre-reset
 * offset, never further down than it. A page's own on-mount scroll is not undone unless it lands
 * exactly on the old offset or the very bottom; any touch, wheel or key ends the guard at once.
 */
export function isStaleScrollEcho(y: number, preResetY: number, maxScroll: number): boolean {
  if (y <= 0 || y > preResetY + 1) return false;
  return Math.abs(y - preResetY) <= 1 || y >= maxScroll - 1;
}

if (typeof window !== "undefined") {
  window.addEventListener("scroll", () => {
    lastScrollAt = Date.now();
    if (echoGuardUntil === 0) return;
    if (Date.now() > echoGuardUntil) { echoGuardUntil = 0; return; }
    const html = document.documentElement;
    const max = Math.max(0, html.scrollHeight - window.innerHeight);
    if (isStaleScrollEcho(window.scrollY, echoFrom, max)) {
      window.scrollTo(0, 0);
      html.scrollTop = 0;
    }
  }, { passive: true });
  // The person took over: never fight them.
  const disarm = () => { echoGuardUntil = 0; };
  for (const ev of ["touchstart", "pointerdown", "wheel", "keydown"]) {
    window.addEventListener(ev, disarm, { passive: true, capture: true });
  }
}

/**
 * Whether to stop a fling with the overflow toggle: only on a touch device, and only while the
 * document is still scrolling. Audit R6-7 (iOS, intermittent): with the toggle on EVERY reset, a
 * screen sometimes opened part-way down (/checklist 80, /live-photos 144, /welcome 607, transport
 * calendar 101) although nothing was scrolling — always the previous offset clamped to the new,
 * still-short page, i.e. WebKit handing the scroll view's old position back when the root became
 * scrollable again. Without a fling there is nothing for the toggle to stop, so it is skipped.
 */
export function shouldStopFling(coarse: boolean, now: number, lastScroll: number): boolean {
  return coarse && now - lastScroll < FLING_WINDOW_MS;
}

/** Scroll the document to the top, stopping an iOS momentum fling first (scrollTo is ignored during one). */
export function resetWindowScroll(): void {
  if (typeof window === "undefined") return;
  const html = document.documentElement;
  if (window.scrollY === 0 && html.scrollTop === 0 && (!document.body || document.body.scrollTop === 0)) return;
  echoFrom = Math.max(window.scrollY, html.scrollTop);
  echoGuardUntil = Date.now() + ECHO_GUARD_MS;
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  // overflow:hidden on the scrolling element ends a WebKit fling immediately; restored on the next
  // frame (touch devices only: on a desktop it would flash the scrollbar away for one frame).
  const stop = shouldStopFling(coarse, Date.now(), lastScrollAt);
  const prev = stop ? html.style.overflow : null;
  if (stop) html.style.overflow = "hidden";
  window.scrollTo(0, 0);
  html.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
  if (stop) requestAnimationFrame(() => {
    html.style.overflow = prev ?? "";
    // The root is scrollable again: WebKit may hand back the fling's own (clamped) offset — the top wins.
    window.scrollTo(0, 0);
  });
}

/** Reset to the top whenever `key` changes (the route path). Runs before paint. */
export function useRouteScrollReset(key: string): void {
  useLayoutEffect(() => { resetWindowScroll(); }, [key]);
}

let lastNavWasPop = false;
/** The path as of the last history change we saw (push, replace or pop). */
let navPath: string | null = typeof window !== "undefined" ? window.location.pathname : null;
if (typeof window !== "undefined") {
  // Registered at import, i.e. before wouter re-renders for the same popstate.
  window.addEventListener("popstate", () => { lastNavWasPop = true; navPath = window.location.pathname; });
  /*
   * Audit R8-1 (iOS app, both phone sizes): screens still opened part-way down, at the PREVIOUS
   * page's offset clamped to the new page's height (/bookings 744 = 1618 - 874, /me/profile
   * 909 = 1576 - 667), with no fling running. The resets below run in a layout effect — AFTER
   * React has swapped the tall old page for the new, short (often still loading) one. By then
   * WebKit's web process has already clamped its own scroll offset to the short page (often to
   * 0, so the reset saw "already at the top" and did nothing) without telling the UI process,
   * whose scroll view still held the old offset and clamped it to the new content size when that
   * arrived. So reset at the NAVIGATION itself: wouter dispatches "pushState"/"replaceState" from
   * its patched history methods synchronously, before React re-renders (wouter v3,
   * use-browser-location.js), while the old page is still in the DOM — the scroll is then an
   * ordinary programmatic scroll on a stable page, and the new page arrives at 0 with nothing
   * to clamp. The layout-effect resets stay as a second pass. Back/forward is untouched.
   */
  const onNavigate = () => {
    const next = window.location.pathname;
    const prev = navPath;
    navPath = next;
    if (shouldResetOnPathChange(prev, next, false)) resetWindowScroll();
  };
  window.addEventListener("pushState", onNavigate);
  window.addEventListener("replaceState", onNavigate);
}

/** Decision for the global reset: push navigations reset, back/forward keeps the browser's offset. */
export function shouldResetOnPathChange(prevPath: string | null, nextPath: string, wasPop: boolean): boolean {
  return prevPath !== null && prevPath !== nextPath && !wasPop;
}

/** Rendered once at the app root: every PUSH route change opens the next screen at the top. */
export function RouteScrollReset(): null {
  const [path] = useLocation();
  const prev = useRef<string | null>(null);
  useLayoutEffect(() => {
    const wasPop = lastNavWasPop;
    lastNavWasPop = false;
    if (shouldResetOnPathChange(prev.current, path, wasPop)) resetWindowScroll();
    prev.current = path;
  }, [path]);
  return null;
}

/**
 * Where to scroll when an in-page tab strip changes tab, or null to stay put. `panelDocTop` is the
 * new panel's top in DOCUMENT coordinates; `stickyAbove` is the height of everything pinned above it
 * (the top bar, plus the strip itself when the strip is sticky). If the reader is already past the
 * panel's start, bring the start back to just under the pinned chrome; otherwise do nothing (no jump
 * for a tap near the top of the page).
 */
export function tabSwitchScrollTarget(scrollY: number, panelDocTop: number, stickyAbove: number): number | null {
  const target = Math.max(0, Math.round(panelDocTop - stickyAbove));
  return scrollY > target ? target : null;
}

const stickyHeight = (el: HTMLElement | null): number => {
  if (!el) return 0;
  const pos = getComputedStyle(el).position;
  return pos === "sticky" || pos === "fixed" ? el.getBoundingClientRect().height : 0;
};

/**
 * Hook form: call with the current tab, a ref to the tab PANEL (never the strip: a stuck sticky strip
 * reports its pinned position, not where it sits in the page) and optionally the strip.
 */
export function useTabSwitchScroll(tab: string, panel: { current: HTMLElement | null }, strip?: { current: HTMLElement | null }): void {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const el = panel.current;
    if (!el || typeof window === "undefined") return;
    const above = stickyHeight(document.querySelector<HTMLElement>('[data-ui="v2"] .shell > .top')) + stickyHeight(strip?.current ?? null);
    const target = tabSwitchScrollTarget(window.scrollY, el.getBoundingClientRect().top + window.scrollY, above);
    if (target !== null) window.scrollTo(0, target);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
}
